import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  IconButton,
  Rating,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress
} from '@mui/material';
import { DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'api/axiosInstance';
import endpoints from '../api/endpoints';

export default function TestimonialsList() {
  const [testimonials, setTestimonials] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [doctorsList, setDoctorsList] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);

  // Fetch testimonials from API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoadingTestimonials(true);
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        const response = await axios.get(endpoints.testimonials.list(tenantId), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Handle different response structures
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.results) {
          data = response.data.results;
        } else if (response.data.data) {
          data = response.data.data;
        }

        setTestimonials(data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoadingTestimonials(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Fetch doctors list from API
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(endpoints.company.doctorList(), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Handle different response structures
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.results) {
          data = response.data.results;
        } else if (response.data.data) {
          data = response.data.data;
        }

        setDoctorsList(data);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchDoctors();
  }, []);

  const handleDelete = (id) => {
    setTestimonials(prev => prev.filter(t => t.id !== id));
  };

  const handleDoctorChange = (event) => {
    setSelectedDoctor(event.target.value);
  };

  const handleClearFilter = () => {
    setSelectedDoctor('');
  };

  // Filter testimonials based on selected doctor
  const filteredTestimonials = selectedDoctor
    ? testimonials.filter(t => t.doctor?.id === selectedDoctor)
    : testimonials;

  // Get selected doctor name for the tag
  const selectedDoctorName = selectedDoctor
    ? doctorsList.find(d => d.id === selectedDoctor)?.name
    : '';

  // Helper function to get user initials for avatar
  const getUserInitials = (user) => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  // Helper function to get full name
  const getFullName = (user) => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Anonymous';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight={600}>
          Testimonials
        </Typography>

        {/* Filter Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="doctor-filter-label">Filter by Doctor</InputLabel>
            <Select
              labelId="doctor-filter-label"
              id="doctor-filter"
              value={selectedDoctor}
              label="Filter by Doctor"
              onChange={handleDoctorChange}
              disabled={loadingDoctors}
            >
              <MenuItem value="">
                <em>{loadingDoctors ? 'Loading...' : 'All Doctors'}</em>
              </MenuItem>
              {doctorsList.map((doctor) => (
                <MenuItem key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loadingDoctors && <CircularProgress size={20} />}
        </Box>
      </Box>

      {/* Selected Doctor Tag */}
      {selectedDoctor && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={selectedDoctorName}
            onDelete={handleClearFilter}
            deleteIcon={<CloseOutlined style={{ fontSize: 12 }} />}
            sx={{
              bgcolor: '#c6922b',
              color: 'white',
              fontWeight: 500,
              '& .MuiChip-deleteIcon': {
                color: 'white',
                '&:hover': {
                  color: '#f5f5f5'
                }
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {filteredTestimonials.length} testimonial{filteredTestimonials.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {loadingTestimonials && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: '#c6922b' }} />
        </Box>
      )}

      {/* Testimonials Grid - 5 items on large, 4 on medium */}
      {!loadingTestimonials && (
        <Grid container spacing={2}>
          {filteredTestimonials.map((testimonial) => (
            <Grid item xs={12} sm={6} md={3} lg={2.4} key={testimonial.id} sx={{ flexBasis: { lg: '20%', md: '25%' }, maxWidth: { lg: '20%', md: '25%' } }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  {/* Avatar and Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Avatar
                      sx={{ width: 48, height: 48, border: '2px solid #c6922b', bgcolor: '#c6922b' }}
                    >
                      {getUserInitials(testimonial.user)}
                    </Avatar>
                    <Box>
                      {/* <IconButton
                        size="small"
                        onClick={() => handleDelete(testimonial.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteOutlined style={{ fontSize: 14 }} />
                      </IconButton> */}
                    </Box>
                  </Box>

                  {/* Name and Role */}
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {getFullName(testimonial.user)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Patient
                  </Typography>

                  {/* Doctor Name */}
                  <Typography variant="caption" display="block" sx={{ color: '#c6922b', fontWeight: 500, mt: 0.5 }}>
                    Consulted: {testimonial.doctor?.name || 'N/A'}
                  </Typography>

                  {/* Star Rating */}
                  <Box sx={{ my: 1 }}>
                    <Rating
                      value={testimonial.rating}
                      readOnly
                      size="small"
                      sx={{
                        '& .MuiRating-iconFilled': { color: '#c6922b' }
                      }}
                    />
                  </Box>

                  {/* Review */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: '12px',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    "{testimonial.feedback}"
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* No Results Message */}
      {!loadingTestimonials && filteredTestimonials.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="body1" color="text.secondary">
            No testimonials found{selectedDoctor ? ' for the selected doctor' : ''}.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
